/**
 * `ConversionStrip` — three small stat cards summarising governance
 * and conversion in the window:
 *   - Discovery → Transactional conversion %
 *   - Approval rate (mandates approved / evaluated)
 *   - Escalations raised vs resolved (mini "ratio" pair)
 *
 * Visualised as a single row of three cards (mirrors the dashboard's
 * compact stat-card idiom). Each card includes the underlying counts
 * so the percentage is interpretable on the screenshot.
 */

import type { AdminMetricsSnapshot } from '@sill/types';

import { Card } from '../../../components/atoms/Card.js';
import { formatCount, formatPct } from '../../../lib/format.js';
import { useC } from '../../../lib/theme.js';
import { SectionHeader } from '../KpiTile.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type ConversionStripState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; snapshot: AdminMetricsSnapshot };

export interface ConversionStripProps {
  state: ConversionStripState;
}

export function ConversionStrip({ state }: ConversionStripProps): JSX.Element {
  const C = useC();

  if (state.kind === 'loading' || state.kind === 'error') {
    return (
      <section>
        <SectionHeader
          kicker="Conversion & governance"
          title="Funnel + guardrails"
        />
        <Card style={{ padding: 18 }}>
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 11,
              color: state.kind === 'error' ? C.coralFg : C.dim,
              letterSpacing: 1,
            }}
          >
            {state.kind === 'loading'
              ? 'Loading…'
              : `Unavailable: ${state.message}`}
          </span>
        </Card>
      </section>
    );
  }

  const s = state.snapshot;
  // Approval bar — clamp pct null → null (shown as "—").
  const approvalPct = s.approval_rate;
  // Conversion bar.
  const conversionPct = s.discovery_to_transactional_conversion;
  // Resolution ratio: avoid div-by-zero.
  const resolveRatio =
    s.escalations_raised === 0
      ? null
      : Math.min(1, s.escalations_resolved / s.escalations_raised);

  return (
    <section>
      <SectionHeader
        kicker="Conversion & governance"
        title="Funnel + guardrails"
        caption="Discovery-to-transactional conversion, mandate approval, and escalation resolution rate over the window."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <StatBarCard
          kicker="Discovery → Transactional"
          pct={conversionPct}
          headline={formatPct(conversionPct)}
          caption={
            <>
              of {formatCount(s.active_accounts)} active accounts ran a
              transactional mandate
            </>
          }
          color={C.limeFg}
        />
        <StatBarCard
          kicker="Approval rate"
          pct={approvalPct}
          headline={formatPct(approvalPct)}
          caption={
            <>
              {formatCount(s.mandates_approved)} of{' '}
              {formatCount(s.mandates_evaluated)} mandates ·{' '}
              <span style={{ color: C.coralFg }}>
                {formatCount(s.mandates_rejected)} rejected
              </span>
            </>
          }
          color={C.mintFg}
        />
        <StatBarCard
          kicker="Escalations resolved"
          pct={resolveRatio}
          headline={
            <>
              {formatCount(s.escalations_resolved)}
              <span style={{ color: C.muted, fontSize: 16, letterSpacing: 0.5 }}>
                {' '}/ {formatCount(s.escalations_raised)}
              </span>
            </>
          }
          caption={
            resolveRatio === null
              ? 'No escalations in window'
              : `${formatPct(resolveRatio, 0)} closed`
          }
          color={C.amberFg}
        />
      </div>
    </section>
  );
}

function StatBarCard({
  kicker,
  pct,
  headline,
  caption,
  color,
}: {
  kicker: string;
  pct: number | null;
  headline: React.ReactNode;
  caption: React.ReactNode;
  color: string;
}): JSX.Element {
  const C = useC();
  const widthPct = pct === null ? 0 : Math.max(0, Math.min(1, pct)) * 100;
  return (
    <Card style={{ padding: 18 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {kicker}
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 24,
          color: C.text,
          fontWeight: 500,
          letterSpacing: -0.5,
          lineHeight: 1.1,
          marginBottom: 12,
        }}
      >
        {headline}
      </div>
      <div
        style={{
          height: 4,
          background: C.surface2,
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 1,
          overflow: 'hidden',
          marginBottom: 8,
        }}
        aria-hidden="true"
      >
        <div
          style={{
            height: '100%',
            width: `${widthPct.toFixed(1)}%`,
            background: color,
            transition: 'width 200ms ease-out',
          }}
        />
      </div>
      <div
        style={{
          fontFamily: sansFont,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
        }}
      >
        {caption}
      </div>
    </Card>
  );
}
